import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Pencil, Plus, Printer, RefreshCw, Search, Trash2 } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { DateInput, MonthInput, Select } from '../components/Controls';
import { download, fmtMoney, fmtNumber, toCSV } from '../format';
import { Chip, STATUS_TONE } from './missed/shared';

// Missed Days Management > Missed Days Register (Stephanet's generic "missed-days" register): every missed day record, detected
// or added by hand. A record may be added for a day the attendance register does not cover, and changed or deleted while it is
// still undecided; decided records are changed through Review + Approval (reverse first). Deletions are kept in the audit log.

const COLS = [['MD ID', r => r.reference], ['Emp ID', r => r.employee_no], ['Name', r => r.name], ['Department', r => r.department],
  ['Date', r => r.date], ['Day', r => r.weekday], ['Days', r => r.days], ['Reason', r => r.reason || ''], ['Source', r => r.source],
  ['Status', r => r.status], ['Leave', r => r.leave_type || ''], ['Deduction', r => r.deduction || ''], ['Reviewed By', r => r.reviewed_by || '']];

export default function MissedDaysRegister() {
  const { notify, canManage } = useHR();
  const [f, setF] = useState({ month: '', status: '', source: '', q: '' });
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => hr.mdRegister({ month: f.month, status: f.status, source: f.source.toLowerCase() })
    .then(x => { setD(x); setError(''); }).catch(e => setError(e.message)), [f.month, f.status, f.source]);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => {
    if (!d) return [];
    const q = f.q.trim().toLowerCase();
    return q ? d.rows.filter(r => [r.name, r.employee_no, r.reference].some(v => String(v || '').toLowerCase().includes(q))) : d.rows;
  }, [d, f.q]);

  if (error) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!d) return <Loader title="Loading the register…" subtitle="Fetching missed day records" />;
  const can = { create: d.can.create && canManage, edit: d.can.edit && canManage, delete: d.can.delete && canManage };

  const save = async body => {
    setBusy(true);
    try {
      const r = editing.id ? await hr.mdRegisterUpdate(editing.id, body) : await hr.mdRegisterCreate(body);
      notify(r.message);
      setEditing(null);
      await load();
    } catch (e) { notify(e.message, 'bad'); }
    setBusy(false);
  };
  const remove = async ok => {
    const r = deleting;
    setDeleting(null);
    if (!ok) return;
    setBusy(true);
    try { notify((await hr.mdRegisterDelete(r.id)).message); await load(); } catch (e) { notify(e.message, 'bad'); }
    setBusy(false);
  };

  return (
    <div className="mdr">
      <div className="mdr-top">
        <div>
          <h2>Missed Days Register</h2>
          <p>Every missed day record — detected from attendance or added by hand — with its source, decision and deduction</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => load().then(() => notify('Register refreshed'))}><RefreshCw size={14} /> Refresh</button>
          {d.can.export && <button className="mdr-btn" disabled={!rows.length} onClick={() => download('missed-days-register.csv', toCSV(COLS.map(([label, get]) => ({ label, get })), rows))}><Download size={14} /> Export</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
          {can.create && <button className="mds-run" onClick={() => setEditing({})}><Plus size={14} /> New Missed Day</button>}
        </div>
      </div>

      <div className="mdr-card mdr-filters no-print">
        <div className="mdr-search"><Search size={15} /><input placeholder="Search employee name, ID or MD ID..." value={f.q} onChange={e => setF({ ...f, q: e.target.value })} /></div>
        <MonthInput className="mdr-month-input" value={f.month} onChange={e => setF({ ...f, month: e.target.value })} />
        <Select value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="">All Statuses</option>{d.statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select value={f.source} onChange={e => setF({ ...f, source: e.target.value })}>
          <option value="">All Sources</option>{d.sources.map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
        <button className="mdr-btn" onClick={() => setF({ month: '', status: '', source: '', q: '' })}>Clear Filter</button>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table">
            <thead><tr>{(can.edit || can.delete) && <th className="no-print">Actions</th>}{COLS.map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                  {(can.edit || can.delete) && (
                    <td className="no-print"><div className="mdr-actions">
                      {can.edit && r.editable && <button className="mdr-act a-blue" title="Edit" disabled={busy} onClick={() => setEditing(r)}><Pencil size={12} /></button>}
                      {can.delete && r.deletable && <button className="mdr-act a-red" title="Delete" disabled={busy} onClick={() => setDeleting(r)}><Trash2 size={12} /></button>}
                    </div></td>
                  )}
                  <td className="mdr-id">{r.reference}</td><td className="mdr-emp">{r.employee_no}</td><td className="mdr-name">{r.name}</td>
                  <td>{r.department}</td><td className="nowrap">{r.date}</td><td>{r.weekday}</td><td>{fmtNumber(r.days)}</td>
                  <td>{r.reason || '—'}</td><td><Chip tone={r.source === 'Manual' ? 'purple' : r.source === 'Biometric' ? 'teal' : 'blue'}>{r.source}</Chip></td>
                  <td className="nowrap"><Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip></td><td>{r.leave_type || '—'}</td>
                  <td className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</td><td>{r.reviewed_by || '—'}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={COLS.length + 1} className="mdr-empty">No missed day records.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot"><span>Showing {rows.length} of {d.rows.length} records</span></div>
      </div>

      {editing && <RecordForm r={editing} employees={d.employees} busy={busy} onClose={() => setEditing(null)} onSave={save} />}
      {deleting && <ConfirmDialog danger title="Delete missed day" confirmLabel="Delete" onAnswer={remove}
                                  message={`Delete ${deleting.reference} (${deleting.name}, ${deleting.date})?`}
                                  detail="The record and its trail are removed; the deletion is kept in the audit log." />}
    </div>
  );
}

function RecordForm({ r, employees, busy, onClose, onSave }) {
  const isNew = !r.id;
  const [v, setV] = useState({ employee_id: r.employee_id || '', date: r.date || '', days: String(r.days || 1), reason: r.reason || '' });
  const set = (k, x) => setV(s => ({ ...s, [k]: x }));
  const submit = e => { e.preventDefault(); onSave({ ...v, days: Number(v.days) }); };
  return (
    <Modal title={isNew ? 'New Missed Day' : `Edit ${r.reference}`} onClose={onClose}>
      <form className="mdr-form" onSubmit={submit}>
        <p className="muted small">{isNew ? 'Record a day the employee was absent that the attendance register does not show. It goes to review like a detected one.'
          : 'An undecided record can be corrected here; the change is kept on its audit trail.'}</p>
        <div className="field">
          <span className="field-label">Employee<em>*</em></span>
          {isNew ? (
            <Select value={v.employee_id} required onChange={e => set('employee_id', e.target.value)}>
              <option value="">Select employee…</option>
              {employees.map(x => <option key={x.id} value={x.id}>{x.name} ({x.employee_no})</option>)}
            </Select>
          ) : <b>{r.name} ({r.employee_no})</b>}
        </div>
        <div className="field"><span className="field-label">Missed Date<em>*</em></span><DateInput value={v.date} required onChange={e => set('date', e.target.value)} /></div>
        <div className="field">
          <span className="field-label">Days</span>
          <Select value={v.days} onChange={e => set('days', e.target.value)}><option value="1">1 — full day</option><option value="0.5">0.5 — half day</option></Select>
        </div>
        <div className="field"><span className="field-label">Reason<em>*</em></span>
          <textarea rows={3} value={v.reason} required onChange={e => set('reason', e.target.value)} placeholder="e.g. Did not report to site; supervisor confirmed" /></div>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || (isNew && !v.employee_id) || !v.date || !v.reason.trim()}>{busy ? 'Saving…' : isNew ? 'Record Missed Day' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  );
}
